import moment from "moment";
import { lookupAnalysed } from "./imageAnalysisService";
import { STATUS_LABEL } from "./constants";

// The badge that marks photographs Otto has already read, for the Image Analyse grid.
//
// The grid itself is Image Management's - shared, and not Otto's to teach about analyses - so the shared
// components take a GENERIC map of `{ imageId: { icon, tip } }` and render whatever is in it. Everything
// that knows what a reading is stays here.
//
// Chunked at the lookup's own ceiling (500 ids per request, `MAX_LOOKUP_IDS` in the backend): the grid loads
// more rows as it scrolls, so the id list outgrows one request on any real library. NEVER REJECTS - a badge
// is decoration, and a failed lookup must not break the grid that carries it.

const CHUNK = 500;

const ICON = {
    ok: "fas fa-magic ia-badge-ok",
    // Anything else is an attempt that produced no reading. Worth marking differently rather than not at
    // all: "Otto tried and could not read this one" is the answer to why a photo has no labels, and without
    // it the only way to find out is to re-analyse it and pay again.
    other: "fas fa-exclamation-triangle ia-badge-failed"
};

const tipFor = entry => {
    const when = entry.analysed_at ? moment(entry.analysed_at).format("MM-DD-YYYY hh:mm A") : "";
    if (entry.status !== "ok") {
        return `Otto: ${STATUS_LABEL[entry.status] || "no reading"}${when ? ` (${when})` : ""}`;
    }
    return [entry.primary_subject ? `Otto read this as ${entry.primary_subject}` : "Analysed by Otto", when, entry.model]
        .filter(Boolean)
        .join(" · ");
};

/** `{ imageId: { icon, tip, status } }` for the photographs in `imageUploadIds` that Otto has analysed. */
export async function loadAnalysedBadges(imageUploadIds = []) {
    const ids = Array.from(new Set(imageUploadIds.filter(Boolean)));
    if (!ids.length) return {};

    const chunks = [];
    for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));

    try {
        const responses = await Promise.all(chunks.map(chunk => lookupAnalysed(chunk)));
        const badges = {};
        responses.forEach(res => {
            const analysed = (res && res.data && res.data.analysed) || {};
            Object.keys(analysed).forEach(id => {
                const entry = analysed[id] || {};
                badges[id] = {
                    status: entry.status,
                    icon: entry.status === "ok" ? ICON.ok : ICON.other,
                    tip: tipFor(entry)
                };
            });
        });
        return badges;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[imageAnalysis] could not load the analysed badges", e && e.response ? e.response.status : e);
        return {};
    }
}

export default loadAnalysedBadges;
